import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Container, Row, Col, Button, Card } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMagic, 
  faHome, 
  faSchool, 
  faMapPin,
  faChartLine,
  faDollarSign,
  faUsers,
  faSwimmingPool,
  faGolfBall,
  faComments
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';

/**
 * Simplified Gilbert Landing Page that works with React
 * No complex imports, just clean SEO-friendly content
 */
const GilbertLandingPageSimple: React.FC = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const handleStartSearch = () => {
    setIsLoading(true);
    // Navigate to AI chat with pre-filled Gilbert context
    navigate('/ai?location=gilbert&message=I%20am%20looking%20for%20homes%20in%20Gilbert%20AZ');
  };

  // Static data for Gilbert
  const stats = {
    totalListings: 1247,
    medianPrice: 579000,
    avgDaysOnMarket: 32,
    newThisWeek: 87
  };

  const topNeighborhoods = [
    { name: 'Val Vista Lakes', avgPrice: '$725K', homes: 42 },
    { name: 'Morrison Ranch', avgPrice: '$585K', homes: 68 },
    { name: 'Agritopia', avgPrice: '$650K', homes: 31 },
    { name: 'Power Ranch', avgPrice: '$515K', homes: 89 },
    { name: 'The Islands', avgPrice: '$850K', homes: 27 },
    { name: 'Trilogy', avgPrice: '$475K', homes: 54 }
  ];

  return (
    <>
      <Helmet>
        <title>Gilbert AZ Real Estate - {stats.totalListings} Homes for Sale | AI-Powered Search</title>
        <meta name="description" content={`Search ${stats.totalListings} Gilbert, AZ homes for sale. Median price $${stats.medianPrice.toLocaleString()}. Find homes in Val Vista Lakes, Morrison Ranch, Agritopia. Free AI-powered search assistant.`} />
        <meta name="keywords" content="Gilbert AZ real estate, homes for sale Gilbert Arizona, Gilbert homes, Val Vista Lakes, Morrison Ranch, Agritopia, Power Ranch" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Gilbert AZ Real Estate - AI-Powered Home Search" />
        <meta property="og:description" content={`Find your dream home in Gilbert, AZ. ${stats.totalListings} active listings with AI-powered search assistance.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://chat.realestate/gilbert" />
        
        {/* Schema markup */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "RealEstateAgent",
            "name": "Gilbert AZ Real Estate Search",
            "url": "https://chat.realestate/gilbert",
            "areaServed": {
              "@type": "City",
              "name": "Gilbert",
              "addressRegion": "AZ"
            },
            "description": "AI-powered real estate search for Gilbert, Arizona homes"
          })}
        </script>
      </Helmet>

      {/* Hero Section */}
      <section className="hero-section py-5" style={{
        background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%)',
        color: 'white'
      }}>
        <Container>
          <Row className="align-items-center">
            <Col lg={6} className="mb-4 mb-lg-0">
              <h1 className="display-4 fw-bold mb-4">
                Find Your Dream Home in Gilbert, AZ
              </h1>
              <p className="lead mb-4">
                Skip the filters. Just tell our AI what you're looking for and explore {stats.totalListings.toLocaleString()} homes in Gilbert.
              </p>
              <div className="mb-4">
                <div className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon icon={faHome} className="me-2" />
                  <span>{stats.newThisWeek} new homes this week</span>
                </div>
                <div className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon icon={faDollarSign} className="me-2" />
                  <span>Median price: ${stats.medianPrice.toLocaleString()}</span>
                </div>
                <div className="d-flex align-items-center">
                  <FontAwesomeIcon icon={faChartLine} className="me-2" />
                  <span>Avg. {stats.avgDaysOnMarket} days on market</span>
                </div>
              </div>
              <Button 
                size="lg" 
                onClick={handleStartSearch}
                disabled={isLoading}
                className="fw-bold shadow"
                style={{
                  background: 'white',
                  color: '#667eea',
                  border: 'none',
                  padding: '12px 30px'
                }}
              >
                <FontAwesomeIcon icon={faComments} className="me-2" />
                {isLoading ? 'Loading...' : 'Start AI Home Search'}
              </Button>
            </Col>
            <Col lg={6}>
              <Card className="shadow-lg" style={{ borderRadius: '15px' }}>
                <Card.Body className="p-4">
                  <h3 className="text-primary mb-3">
                    <FontAwesomeIcon icon={faMagic} className="me-2" />
                    How It Works
                  </h3>
                  <div className="mb-3">
                    <strong>1. Tell us what you want</strong>
                    <p className="text-muted mb-2">"I need a 4 bedroom home with a pool near good schools"</p>
                  </div>
                  <div className="mb-3">
                    <strong>2. AI searches all {stats.totalListings} Gilbert listings</strong>
                    <p className="text-muted mb-2">No complex filters or forms to fill out</p>
                  </div>
                  <div>
                    <strong>3. See personalized matches instantly</strong>
                    <p className="text-muted mb-0">Refine results by chatting naturally</p>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Popular Neighborhoods */}
      <section className="py-5">
        <Container>
          <h2 className="text-center mb-5">Popular Gilbert Neighborhoods</h2>
          <Row>
            {topNeighborhoods.map((neighborhood) => (
              <Col md={4} key={neighborhood.name} className="mb-4">
                <Card className="h-100 shadow-sm neighborhood-card">
                  <Card.Body>
                    <h4>{neighborhood.name}</h4>
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <span className="text-muted">Avg. Price</span>
                      <strong className="text-primary">{neighborhood.avgPrice}</strong>
                    </div>
                    <div className="d-flex align-items-center text-success">
                      <FontAwesomeIcon icon={faHome} className="me-2" />
                      <span>{neighborhood.homes} homes available</span>
                    </div>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      className="mt-3 w-100"
                      onClick={() => navigate(`/ai?location=gilbert&neighborhood=${neighborhood.name}`)}
                    >
                      Search {neighborhood.name}
                    </Button>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Why Gilbert Section */}
      <section className="py-5 bg-light">
        <Container>
          <Row>
            <Col lg={6} className="mb-4">
              <h2 className="mb-4">Why Families Love Gilbert</h2>
              <div className="mb-3">
                <div className="d-flex align-items-start">
                  <FontAwesomeIcon icon={faSchool} className="text-primary me-3 mt-1" />
                  <div>
                    <h5>Top-Rated Schools</h5>
                    <p className="text-muted">Home to some of Arizona's best public and charter schools</p>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex align-items-start">
                  <FontAwesomeIcon icon={faUsers} className="text-primary me-3 mt-1" />
                  <div>
                    <h5>Family-Friendly Community</h5>
                    <p className="text-muted">Safe neighborhoods with parks, trails, and community events</p>
                  </div>
                </div>
              </div>
              <div className="mb-3">
                <div className="d-flex align-items-start">
                  <FontAwesomeIcon icon={faSwimmingPool} className="text-primary me-3 mt-1" />
                  <div>
                    <h5>Resort-Style Living</h5>
                    <p className="text-muted">Many communities with pools, golf courses, and amenities</p>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6}>
              <Card className="shadow h-100">
                <Card.Body className="p-4">
                  <h3 className="mb-4">Quick Gilbert Stats</h3>
                  <Row>
                    <Col xs={6} className="mb-3">
                      <div className="text-center">
                        <h4 className="text-primary">{stats.totalListings}</h4>
                        <small className="text-muted">Active Listings</small>
                      </div>
                    </Col>
                    <Col xs={6} className="mb-3">
                      <div className="text-center">
                        <h4 className="text-primary">${(stats.medianPrice / 1000).toFixed(0)}K</h4>
                        <small className="text-muted">Median Price</small>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-center">
                        <h4 className="text-primary">330K+</h4>
                        <small className="text-muted">Population</small>
                      </div>
                    </Col>
                    <Col xs={6}>
                      <div className="text-center">
                        <h4 className="text-primary">#2</h4>
                        <small className="text-muted">Safest City in AZ</small>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="py-5 text-center" style={{ background: '#f8f9fa' }}>
        <Container>
          <h2 className="mb-4">Ready to Find Your Gilbert Home?</h2>
          <p className="lead mb-4">
            Our AI assistant helps you search all {stats.totalListings} listings in seconds
          </p>
          <Button 
            size="lg" 
            onClick={handleStartSearch}
            className="fw-bold shadow"
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              border: 'none',
              padding: '12px 40px'
            }}
          >
            <FontAwesomeIcon icon={faMagic} className="me-2" />
            Search Gilbert Homes with AI
          </Button>
        </Container>
      </section>

      <style>{`
        .neighborhood-card {
          transition: transform 0.3s, box-shadow 0.3s;
        }
        .neighborhood-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1) !important;
        }
      `}</style>
    </>
  );
};

export default GilbertLandingPageSimple;